import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsService } from '@/services/analytics';
import { apiService } from '@/services/api';
import { 
  ReportSetupForm, 
  DateRangeSelector, 
  ReportsList, 
  AnalyticsStatus 
} from './components';
import { Report, ReportSetup } from './types';

const mockReports: Report[] = [
  {
    id: '1',
    title: 'Keto Diet Keywords Analysis',
    type: 'keyword',
    status: 'completed',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    description: 'Comprehensive analysis of keto diet related keywords and their performance metrics.'
  },
  {
    id: '2',
    title: 'Competitor Analysis - Health & Fitness',
    type: 'competitor',
    status: 'completed',
    createdAt: '2024-01-14T14:20:00Z',
    updatedAt: '2024-01-14T14:20:00Z',
    description: 'Detailed competitor analysis for health and fitness niche websites.'
  },
  {
    id: '3',
    title: 'Q1 2024 SEO Trends Report',
    type: 'trend',
    status: 'processing',
    createdAt: '2024-01-13T09:15:00Z',
    updatedAt: '2024-01-13T09:15:00Z',
    description: 'Quarterly trends analysis for SEO and digital marketing strategies.'
  },
  {
    id: '4',
    title: 'Custom Market Research',
    type: 'custom',
    status: 'failed',
    createdAt: '2024-01-12T16:45:00Z',
    updatedAt: '2024-01-12T16:45:00Z',
    description: 'Custom market research report for e-commerce sector.'
  }
];

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [reports] = useState<Report[]>(mockReports);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('custom');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  
  // Analytics data state
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  // Report setup form state
  const [reportSetup, setReportSetup] = useState<ReportSetup>({
    clientName: '',
    clientWebsite: '',
    companyName: '',
    companyAddress: '',
    companySubtext: ''
  });

  // Generate localStorage key for analytics data
  const getAnalyticsDataKey = useCallback((startDate: string, endDate: string) => {
    return `analytics_data_${startDate}_${endDate}`;
  }, []);

  // Check if data exists in localStorage for today
  const getCachedAnalyticsData = useCallback((startDate: string, endDate: string) => {
    const key = getAnalyticsDataKey(startDate, endDate);
    const cached = localStorage.getItem(key);
    
    if (cached) {
      try {
        const data = JSON.parse(cached);
        // Check if data is from today
        const today = new Date().toISOString().split('T')[0];
        if (data.cachedDate === today) {
          return data.analyticsData;
        }
      } catch (error) {
        console.error('Error parsing cached analytics data:', error);
        localStorage.removeItem(key);
      }
    }
    return null;
  }, [getAnalyticsDataKey]);

  // Store analytics data in localStorage
  const cacheAnalyticsData = useCallback((startDate: string, endDate: string, data: any) => {
    const key = getAnalyticsDataKey(startDate, endDate);
    const cacheData = {
      analyticsData: data,
      cachedDate: new Date().toISOString().split('T')[0]
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
    console.log(`Cached analytics data with key: ${key}`);
  }, [getAnalyticsDataKey]);


  // Fetch analytics data using the same pattern as Dashboard
  const fetchAnalyticsData = useCallback(async () => {
    try {
      setIsLoadingAnalytics(true);
      setAnalyticsError(null);
      
      console.log('Starting analytics data fetch...');
      
      // Check if user has an active analytics connection
      const activeConnection = await analyticsService.getActiveConnection();
      console.log('Active connection:', activeConnection);
      
      
      if (!activeConnection) {
        console.log('No active connection found');
        setAnalyticsError('No active analytics connection found. Please connect your Google Analytics account first.');
        return;
      }

      // Always fetch data for the entire year
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const startDateParam = startOfYear.toISOString().split('T')[0];
      const endDateParam = today.toISOString().split('T')[0];

      console.log('Date range:', { startDateParam, endDateParam });

      // Check if data exists in localStorage for today
      const cachedData = getCachedAnalyticsData(startDateParam, endDateParam);
      
      if (cachedData) {
        console.log('Using cached analytics data for today');
        setAnalyticsData(cachedData);
        setIsLoadingAnalytics(false);
        return;
      }

      console.log('Fetching fresh analytics data for the year');
      
      // Use the same API pattern as Dashboard - fetch daily visits data
      const dailyVisitsResponse = await apiService.get('/analytics/daily-visits/', {
        params: { 
          period: '365', // Get 1 year of data
          group_by: 'day'
        }
      });
      
      console.log('Daily Visits API response:', dailyVisitsResponse.data);
      
      // Process the data similar to Dashboard
      const rawData = dailyVisitsResponse.data;
      let processedData = null;
      
      if (rawData?.data && typeof rawData.data === 'object') {
        // Calculate aggregated metrics for the year
        const dataEntries = Object.values(rawData.data).filter((item: any) => 
          item && typeof item === 'object' && item.date
        );
        
        if (dataEntries.length > 0) {
          const totalVisitors = dataEntries.reduce((sum: number, item: any) => sum + (item.users || 0), 0);
          const totalPageViews = dataEntries.reduce((sum: number, item: any) => sum + (item.page_views || 0), 0);
          const totalSessions = dataEntries.reduce((sum: number, item: any) => sum + (item.sessions || 0), 0);
          const avgBounceRate = dataEntries.reduce((sum: number, item: any) => sum + (item.bounce_rate || 0), 0) / dataEntries.length;
          
          processedData = {
            visitors: totalVisitors,
            pageViews: totalPageViews,
            sessions: totalSessions,
            bounce_rate: Number(avgBounceRate.toFixed(2)),
            rawData: rawData.data // Keep raw data for potential future use
          };
        }
      }
      
      console.log('Processed analytics data:', processedData);
      
      // Cache the data
      cacheAnalyticsData(startDateParam, endDateParam, processedData);
      
      setAnalyticsData(processedData);
    } catch (error: any) {
      console.error('Error fetching analytics data:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to fetch analytics data. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          errorMessage = 'Authentication failed. Please reconnect your Google Analytics account.';
        } else if (status === 403) {
          errorMessage = 'Access denied. Please check your Google Analytics permissions.';
        } else if (status === 404) {
          errorMessage = 'Analytics connection not found. Please reconnect your account.';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (data?.message) {
          errorMessage = data.message;
        } else {
          errorMessage = `API Error (${status}): ${data?.detail || 'Unknown error'}`;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your internet connection.';
      } else {
        // Something else happened
        errorMessage = error.message || 'An unexpected error occurred.';
      }
      
      setAnalyticsError(errorMessage);
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, [getCachedAnalyticsData, cacheAnalyticsData]);

  // Fetch analytics data on component mount only
  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const isGenerateDisabled = !startDate && !endDate && !selectedMonth && !['last7days', 'last30days', 'last3months', 'last6months', 'lastyear'].includes(dateRange);

  const handleGenerateReport = () => {
    // Prepare date range data
    const reportData = {
      dateRange,
      startDate: dateRange === 'custom' ? startDate : null,
      endDate: dateRange === 'custom' ? endDate : null,
      selectedMonth: dateRange === 'month' ? selectedMonth : null,
      analyticsData: analyticsData, // Pass analytics data to the generate page
      reportSetup: reportSetup // Pass report setup data
    };

    // Navigate to report generation page with data
    navigate('/reports/generate', { 
      state: { reportData } 
    });
  };

  const handleViewReport = (reportId: string) => {
    navigate(`/reports/${reportId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Reports</h1>
          <p className="text-muted-foreground">
            Generate new reports and manage existing ones
          </p>
        </div>
      </div>

      {/* Analytics Status */}
      <AnalyticsStatus
        analyticsError={analyticsError}
        isLoadingAnalytics={isLoadingAnalytics}
        onRetry={fetchAnalyticsData}
        onConnectAnalytics={() => navigate('/dashboard/connect')}
      />

      {/* Report Setup Form */}
      <ReportSetupForm
        reportSetup={reportSetup}
        onReportSetupChange={setReportSetup}
      />

      {/* Date Range Selection */}
      <DateRangeSelector
        dateRange={dateRange}
        startDate={startDate}
        endDate={endDate}
        selectedMonth={selectedMonth}
        onDateRangeChange={setDateRange}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onSelectedMonthChange={setSelectedMonth}
        onGenerateReport={handleGenerateReport}
        isGenerateDisabled={isGenerateDisabled}
      />

      {/* Reports List */}
      <ReportsList
        reports={reports}
        searchTerm={searchTerm}
        filterType={filterType}
        filterStatus={filterStatus}
        onSearchChange={setSearchTerm}
        onFilterTypeChange={setFilterType}
        onFilterStatusChange={setFilterStatus}
        onViewReport={handleViewReport}
        onGenerateReport={handleGenerateReport}
      />
    </div>
  );
};

export default Reports;
